import { makeFormOptions, useAppForm } from "@/components/form";
import { isDefinedError } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { Anchor, Button, Divider, Input, Paper, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { AdminAuth } from "@workspace/admin-domain/src/features/auth/schema";
import { t } from "i18next";

export const Route = createFileRoute("/login/")({
  component: RouteComponent,
  beforeLoad: async () => {
    if (CONSTANTS.DEMO) {
      document.cookie = [
        `session=00000000-0000-4000-8000-000000000107.rehla-demo-session-2026`,
        `Domain=${import.meta.env.VITE_PUBLIC_DOMAIN}`,
        `expires=${new Date(8640000000000000).toUTCString()}`,
        "path=/",
        "SameSite=Lax",
        "Secure",
      ].join("; ");
      window.location.href = "/";
    }
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const router = useRouter();

  const ownerMutation = useMutation(
    orpc.auth.signIn.owner.handler.mutationOptions({
      onSuccess: ({ url }) => {
        window.location.assign(url);
      },
    }),
  );

  const employeeMutation = useMutation(
    orpc.auth.signIn.employee.mutationOptions({
      onError: (error) => {
        if (isDefinedError(error) && error.code === "BAD_REQUEST") {
          form.setErrorMap({
            onChange: {
              form: {
                message: error.message,
              },
              fields: {},
            },
          });
          return;
        }

        notifications.show({
          title: error.message || t("unknownError"),
          message: t("error.description"),
          color: "red",
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        await router.invalidate();
        window.location.assign("/");
      },
    }),
  );

  const form = useAppForm({
    ...makeFormOptions({
      defaultValues: {
        agency: "",
        username: "",
        password: "",
      },
      schema: AdminAuth.UsernameSignIn,
    }),
    onSubmit: async ({ value }) => {
      await employeeMutation.mutateAsync(value);
    },
  });

  return (
    <div
      className="min-h-screen bg-cover"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1484242857719-4b9144542727?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1280&q=80)",
      }}
    >
      <Paper className="px-xl pb-xl mx-auto flex min-h-screen w-full flex-col rounded-none border-r pt-40 sm:mx-0 sm:max-w-112.5">
        <div className="gap-xl flex flex-col">
          {/* Welcome and headline */}
          <div className="flex flex-col gap-1.5">
            <Title order={2}>{t("login.title")}</Title>
            <Text size="lg" c="gray">
              {t("login.description")}
            </Text>
          </div>

          {/* Google login call-to-action */}
          <div>
            <Button
              fullWidth
              leftSection={<GoogleIcon />}
              size="md"
              variant="outline"
              className="font-medium"
              loading={ownerMutation.isPending}
              onClick={async () => await ownerMutation.mutateAsync({})}
              disabled={CONSTANTS.DEMO}
            >
              {t("login.continueWithGoogle")}
            </Button>
          </div>

          <Divider label="OR" />

          {/* Email/password form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="gap-lg flex w-full flex-col"
            noValidate
          >
            <form.AppField name="agency">
              {(field) => (
                <field.TextField
                  size="md"
                  maxLength={5}
                  label={t("agency.username.title")}
                  classNames={{
                    input: "uppercase",
                  }}
                />
              )}
            </form.AppField>

            <form.AppField name="username">
              {(field) => <field.TextField size="md" label={t("login.username")} />}
            </form.AppField>

            <form.AppField name="password">
              {(field) => <field.PasswordField size="md" label={t("login.password")} />}
            </form.AppField>

            <form.Subscribe selector={(state) => state.errors}>
              {(errors) => (
                <Input.Error className="empty:hidden">
                  {typeof errors[0]?.message === "string" ? errors[0]?.message : null}
                </Input.Error>
              )}
            </form.Subscribe>

            <form.AppForm>
              <form.SubmitButton size="md" disabled={CONSTANTS.DEMO}>
                {t("login.login")}
              </form.SubmitButton>
            </form.AppForm>
          </form>

          {/* Alternative register route */}
          <Text className="text-center">
            {t("login.noAccount")}{" "}
            <Anchor href="#" fw={600}>
              {t("login.register")}
            </Anchor>
          </Text>
        </div>
      </Paper>
    </div>
  );
}

function GoogleIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid"
      viewBox="0 0 256 262"
      className="size-6"
      {...props}
    >
      <path
        fill="#4285F4"
        d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
      />
      <path
        fill="#34A853"
        d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
      />
      <path
        fill="#FBBC05"
        d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
      />
      <path
        fill="#EB4335"
        d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
      />
    </svg>
  );
}
